import React from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  Stack,
  Divider,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  Search,
  ArrowBackIosNew,
  Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useGetInvestorUserDataQuery,
  useGetOwnerUserDataQuery,
} from "../../../redux/api/authApi";
import AdminUsersSkeleton from "./AdminUsersSkeleton";

const STATUS_CHIP_KEY = {
  Verified: "success",
  Pending: "pending",
  Active: "info",
  Inactive: "neutral",
};

const badge = (label, type) => {
  const key = STATUS_CHIP_KEY[type || label] || "pending";
  return (
    <Chip
      label={label}
      sx={{
        fontSize: 11,
        fontWeight: 600,
        height: 24,
        px: 0.5,
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        borderRadius: 999,
      }}
      size="small"
    />
  );
};

const getFullName = (u) => {
  if (u.firstName || u.lastName) {
    return `${u.firstName || ""} ${u.lastName || ""}`.trim();
  }
  return u.name || "-";
};

const getLocation = (u) => {
  const parts = [u.city, u.state, u.country].filter(Boolean);
  return parts.join(", ") || "-";
};

/**
 * Paged list API: { success, message, data: { page, limit, total, totalPages, data: User[] } }
 * or legacy: { data: User[] }.
 * Asset owners from get-owners include totalAssetsValue, assetsCreatedCount, currency;
 * assetsOwned is an onboarding range string (e.g. "2-5"), not a listing count.
 */
function unwrapPagedUserList(res) {
  if (!res?.data) {
    return { items: [], total: 0, page: 1, totalPages: 1, limit: 10 };
  }
  const d = res.data;
  if (Array.isArray(d)) {
    return {
      items: d,
      total: d.length,
      page: 1,
      totalPages: 1,
      limit: d.length || 10,
    };
  }
  const items = Array.isArray(d.data) ? d.data : [];
  const total = Number(d.total) >= 0 ? Number(d.total) : items.length;
  const limit = Number(d.limit) > 0 ? Number(d.limit) : items.length || 10;
  const totalPages =
    Number(d.totalPages) > 0
      ? Number(d.totalPages)
      : Math.max(1, Math.ceil(total / limit));
  return {
    items,
    total,
    page: d.page ?? 1,
    totalPages,
    limit,
  };
}

function formatInvestedAmount(amount, currency) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  const c = currency && String(currency).trim() ? String(currency).trim() : "SAR";
  return `${c} ${num.toLocaleString()}`;
}

export default function AdminUsers() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = React.useState("investors");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [kycFilter, setKycFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const [investorTablePage, setInvestorTablePage] = React.useState(0);
  const [investorRowsPerPage, setInvestorRowsPerPage] = React.useState(10);
  const [ownerTablePage, setOwnerTablePage] = React.useState(0);
  const [ownerRowsPerPage, setOwnerRowsPerPage] = React.useState(10);

  const investorQueryArg = React.useMemo(
    () => ({
      page: investorTablePage + 1,
      limit: investorRowsPerPage,
      search: searchTerm,
      kycStatus: kycFilter,
      status: statusFilter,
    }),
    [
      investorTablePage,
      investorRowsPerPage,
      searchTerm,
      kycFilter,
      statusFilter,
    ],
  );
  const ownerQueryArg = React.useMemo(
    () => ({
      page: ownerTablePage + 1,
      limit: ownerRowsPerPage,
      search: searchTerm,
      kycStatus: kycFilter,
      status: statusFilter,
    }),
    [ownerTablePage, ownerRowsPerPage, searchTerm, kycFilter, statusFilter],
  );

  const skipInvestorsQuery = activeTab !== "investors";
  const skipOwnersQuery = activeTab !== "owners";

  const {
    data: investorRes,
    isLoading: investorsLoading,
    isError: investorsError,
  } = useGetInvestorUserDataQuery(investorQueryArg, {
    skip: skipInvestorsQuery,
  });

  const {
    data: ownerRes,
    isLoading: ownersLoading,
    isError: ownersError,
  } = useGetOwnerUserDataQuery(ownerQueryArg, {
    skip: skipOwnersQuery,
  });

  const investorListMeta = React.useMemo(
    () => unwrapPagedUserList(investorRes),
    [investorRes],
  );
  const ownerListMeta = React.useMemo(
    () => unwrapPagedUserList(ownerRes),
    [ownerRes],
  );

  const investors = investorListMeta.items;
  const owners = ownerListMeta.items;
  const investorTotalCount = investorListMeta.total;
  const ownerTotalCount = ownerListMeta.total;

  /** Last successful totals for tab badges while the other list query is skipped */
  const [investorTabBadgeTotal, setInvestorTabBadgeTotal] = React.useState(0);
  const [ownerTabBadgeTotal, setOwnerTabBadgeTotal] = React.useState(0);

  React.useEffect(() => {
    if (skipInvestorsQuery || investorsLoading || investorsError || investorRes == null) {
      return;
    }
    setInvestorTabBadgeTotal(investorTotalCount);
  }, [
    skipInvestorsQuery,
    investorsLoading,
    investorsError,
    investorRes,
    investorTotalCount,
  ]);

  React.useEffect(() => {
    if (skipOwnersQuery || ownersLoading || ownersError || ownerRes == null) {
      return;
    }
    setOwnerTabBadgeTotal(ownerTotalCount);
  }, [
    skipOwnersQuery,
    ownersLoading,
    ownersError,
    ownerRes,
    ownerTotalCount,
  ]);

  const rawList = activeTab === "investors" ? investors : owners;

  React.useEffect(() => {
    setInvestorTablePage(0);
    setOwnerTablePage(0);
  }, [searchTerm, kycFilter, statusFilter]);

  React.useEffect(() => {
    if (skipInvestorsQuery) return;
    const max = Math.max(0, (investorListMeta.totalPages || 1) - 1);
    setInvestorTablePage((p) => (p > max ? max : p));
  }, [skipInvestorsQuery, investorListMeta.totalPages]);

  React.useEffect(() => {
    if (skipOwnersQuery) return;
    const max = Math.max(0, (ownerListMeta.totalPages || 1) - 1);
    setOwnerTablePage((p) => (p > max ? max : p));
  }, [skipOwnersQuery, ownerListMeta.totalPages]);

  const loading =
    activeTab === "investors" ? investorsLoading : ownersLoading;
  const error = activeTab === "investors" ? investorsError : ownersError;

  const paginationTotal =
    activeTab === "investors" ? investorTotalCount : ownerTotalCount;
  const paginationPage =
    activeTab === "investors" ? investorTablePage : ownerTablePage;
  const paginationRowsPerPage =
    activeTab === "investors" ? investorRowsPerPage : ownerRowsPerPage;

  const handleChangePage = (_event, newPage) => {
    if (activeTab === "investors") setInvestorTablePage(newPage);
    else setOwnerTablePage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    if (activeTab === "investors") {
      setInvestorRowsPerPage(next);
      setInvestorTablePage(0);
    } else {
      setOwnerRowsPerPage(next);
      setOwnerTablePage(0);
    }
  };

  const handleTabChange = React.useCallback((nextTab) => {
    if (nextTab === activeTab) return;
    setActiveTab(nextTab);
    // Reset optional filters so next tab request starts with base payload.
    setSearchTerm("");
    setKycFilter("all");
    setStatusFilter("all");
    setInvestorTablePage(0);
    setOwnerTablePage(0);
  }, [activeTab]);

  const goToUserDetail = (u) => {
    if (!u?._id) return;
    const path =
      activeTab === "investors"
        ? `/admin/users/investor/${u._id}`
        : `/admin/users/owner/${u._id}`;
    navigate(path);
  };
  return (
    <Box>
      {/* Back link */}
      <Button
        onClick={() => navigate("/admin")}
        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Dashboard
      </Button>

      {/* Page header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", letterSpacing: -0.5 }}>
            User Management
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
            Manage investors and asset owners
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Download />}
          sx={{
            textTransform: "none",
            fontSize: 13,
            bgcolor: (theme) => theme.palette.accent.teal.action,
            borderRadius: 1,
            px: 2.5,
            py: 1,
            "&:hover": {
              bgcolor: (theme) => theme.palette.accent.teal.actionHover,
            },
          }}
        >
          Export Users
        </Button> */}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 1.5,
          overflow: "hidden",
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
        }}
      >
        {/* Card header: All Users + filters */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 3, pt: 3, pb: 2 }}
        >
          <Typography fontSize={16} fontWeight={700} sx={{ color: "text.primary" }}>
            All Users
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 240,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  bgcolor: "background.paper",
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={kycFilter}
                onChange={(e) => setKycFilter(e.target.value)}
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value="all">All KYC</MenuItem>
                <MenuItem value="Verified">Verified</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 1 }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {/* Tab pill group */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Stack
            direction="row"
            sx={{
              display: "inline-flex",
              p: 0.5,
              borderRadius: 999,
              bgcolor: "background.subtle",
              border: (theme) => `1px solid ${theme.palette.border.light}`,
            }}
          >
            <Button
              size="small"
              onClick={() => handleTabChange("investors")}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                px: 2.5,
                py: 0.6,
                borderRadius: 999,
                minWidth: 0,
                bgcolor:
                  activeTab === "investors" ? "background.paper" : "transparent",
                color:
                  activeTab === "investors" ? "text.primary" : "text.muted",
                boxShadow:
                  activeTab === "investors"
                    ? "0 1px 2px rgba(15,23,42,0.08)"
                    : "none",
                "&:hover": {
                  bgcolor:
                    activeTab === "investors"
                      ? "background.paper"
                      : "transparent",
                },
              }}
            >
              Investors
            </Button>
            <Button
              size="small"
              onClick={() => handleTabChange("owners")}
              sx={{
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
                px: 2.5,
                py: 0.6,
                borderRadius: 999,
                minWidth: 0,
                bgcolor:
                  activeTab === "owners" ? "background.paper" : "transparent",
                color:
                  activeTab === "owners" ? "text.primary" : "text.muted",
                boxShadow:
                  activeTab === "owners"
                    ? "0 1px 2px rgba(15,23,42,0.08)"
                    : "none",
                "&:hover": {
                  bgcolor:
                    activeTab === "owners"
                      ? "background.paper"
                      : "transparent",
                },
              }}
            >
              Asset Owners
            </Button>
          </Stack>
        </Box>

        <Divider />

        {loading ? (
          <AdminUsersSkeleton />
        ) : error ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="error" fontSize={14}>
              Failed to load users. Please try again.
            </Typography>
          </Box>
        ) : (
          <>
            {rawList.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography fontSize={14} color="text.secondary">
                  {paginationTotal === 0
                    ? "No users found."
                    : "No users on this page."}
                </Typography>
              </Box>
            ) : (
              <Table>
            <TableHead sx={{ bgcolor: (theme) => theme.palette.table.headBg }}>
              <TableRow>
                {[
                  "Serial No.",
                  activeTab === "investors" ? "User" : "Owner",
                  "Location",
                  activeTab === "investors" ? "Total Invested" : "Total Value",
                  "Total Assets",
                  "KYC Status",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: (theme) => theme.palette.table.headText,
                      py: 1.5,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rawList.map((u) => {
                const idxInPage = rawList.findIndex((row) => row === u);
                const serialNo =
                  idxInPage >= 0
                    ? paginationPage * paginationRowsPerPage + idxInPage + 1
                    : "—";

                const fullName = getFullName(u);
                const email = u.email || "-";
                const location = getLocation(u);

                const assetsCount =
                  activeTab === "owners"
                    ? (() => {
                        const n = Number(u.assetsCreatedCount);
                        return Number.isFinite(n) ? n : "—";
                      })()
                    : u.totalAssetsInvested ?? u.assetsInvested ?? u.assetsCount ?? "-";

                const totalValue =
                  activeTab === "investors"
                    ? formatInvestedAmount(
                        u.totalInvestedAmount ?? u.totalInvested,
                        'SAR',
                      )
                    : formatInvestedAmount(
                        u.totalAssetsValue ?? u.totalValue ?? u.netWorth,
                        'SAR',
                      );

                const kycStatus = u.kycStatus || "Pending";
                const status = u.status || "Inactive";

                return (
                  <TableRow
                    key={u._id || email}
                    hover
                    onClick={() => goToUserDetail(u)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: (theme) => theme.palette.table.rowHover },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {serialNo}
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>
                          {(fullName || email || "U")[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontSize={14} fontWeight={600} sx={{ color: "text.primary" }}>
                            {fullName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.muted" }}>
                            {email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ fontSize: 14, color: "text.primary" }}>
                      {location}
                    </TableCell>

                    <TableCell sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                      {totalValue}
                    </TableCell>

                    <TableCell sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                      {assetsCount}
                    </TableCell>

                    <TableCell>{badge(kycStatus, kycStatus)}</TableCell>

                    <TableCell>{badge(status, status)}</TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          sx={{
                            color: (theme) => theme.palette.accent.teal.action,
                            "&:hover": {
                              bgcolor: (theme) => theme.palette.accent.teal.chipBg,
                            },
                          }}
                          onClick={() => goToUserDetail(u)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
            )}

            {paginationTotal > 0 ? (
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={paginationTotal}
                rowsPerPage={paginationRowsPerPage}
                page={paginationPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  "& .MuiTablePagination-toolbar": { px: 2, py: 1 },
                }}
              />
            ) : null}
          </>
        )}
      </Paper>

    </Box>
  );
}
